import { Alert, FormControl, InputLabel, MenuItem, Select, Stack } from '@mui/material';
import { useTranslations } from '@/i18n/use-translations.tsx';
import type { TranslationKey } from '@/i18n/translation-key.tsx';
import type {
  AvSourceField,
  AvSourceSelectablePlayer,
  AvSourceWarning,
} from '@/types/dtos/player/response/av-sources.dto';
import { ConfigurationInfoTitle } from './configuration-info-title';

export const AV_SOURCE_NONE = '__none__';

type AvSourceSelectProps = {
  field: AvSourceField | 'audience';
  title: string;
  intro: string;
  value: string | null;
  players: AvSourceSelectablePlayer[];
  warnings: AvSourceWarning[];
  disabled?: boolean;
  allowNone?: boolean;
  onChange: (playerId: string | null) => void;
};

export function AvSourceSelect({
  field,
  title,
  intro,
  value,
  players,
  warnings,
  disabled,
  allowNone = true,
  onChange,
}: AvSourceSelectProps) {
  const t = useTranslations();
  const labelId = `av-source-${field}-label`;
  const fieldWarnings = warnings.filter((warning) => warning.field === field);

  return (
    <Stack spacing={1.5}>
      <ConfigurationInfoTitle title={title} info={intro} />
      <FormControl size="small" fullWidth disabled={disabled}>
        <InputLabel id={labelId}>{title}</InputLabel>
        <Select
          labelId={labelId}
          value={value ?? AV_SOURCE_NONE}
          label={title}
          onChange={(e) => {
            const next = e.target.value;
            onChange(next === AV_SOURCE_NONE ? null : next);
          }}
        >
          {allowNone ? <MenuItem value={AV_SOURCE_NONE}>{t('flows.detail.configuration.inputs.none')}</MenuItem> : null}
          {players.map((player) => {
            const typeKey = `flows.detail.configuration.playerType.${player.type}` as TranslationKey;
            const typeLabel = t(typeKey);
            const name = player.name?.trim();
            return (
              <MenuItem key={player.id} value={player.id}>
                {name ? `${name} (${typeLabel})` : typeLabel}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
      {fieldWarnings.map((warning) => {
        const key = `flows.detail.configuration.inputs.warnings.${warning.code}_${field}` as TranslationKey;
        return (
          <Alert key={`${warning.code}-${warning.playerId ?? field}`} severity="warning">
            {t(key)}
          </Alert>
        );
      })}
    </Stack>
  );
}
